/**
 * メール確認ページ (Phase 3 — Figma 準拠).
 *
 * URL: /verify-email?token=xxx
 * 遷移時に自動で GET /auth/verify-email?token=xxx を呼ぶ。
 * Figma: #35 (確認完了), #71 (リンク切れ)
 */

import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { authVerifyEmail } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';
import { trackEvent } from '../lib/posthog.js';

type VerifyState = 'verifying' | 'success' | 'error' | 'invalid';

/**
 * URL から `token` パラメータを消去する。
 *
 * PR-VERIFY-RESET-CLEANUP: 成功 / 失敗どちらの後も、ブラウザ history / 共有された
 * URL / referrer ヘッダ経由で token が外部に漏れないように `?token=...` を取り除く。
 * SPA 内なので window.location.replace は使わず、history.replaceState で
 * URL だけ書き換える (再レンダー誘発しない)。
 */
function scrubTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('token')) return;
  url.searchParams.delete('token');
  const next = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

export function VerifyEmailPage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';

  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'invalid');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    document.title = 'メール確認 - PetaCMS';
  }, []);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }

    let cancelled = false;
    authVerifyEmail(token)
      .then(() => {
        if (cancelled) return;
        trackEvent('email_verified');
        setState('success');
        // PR-VERIFY-RESET-CLEANUP: 成功後は URL から token を消去 (history / referrer / 共有リンク経由の漏洩を防ぐ)
        scrubTokenFromUrl();
      })
      .catch((err) => {
        if (cancelled) return;
        setState('error');
        setErrorMsg(formatErrorMessage(err, 'メール確認に失敗しました'));
        // PR-VERIFY-RESET-CLEANUP: 失敗時 (期限切れ等) も token は機微なので URL から消去
        scrubTokenFromUrl();
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-[400px] rounded-[12px] border border-[#edebe8] bg-white px-8 py-10 text-center">
        <h1 className="text-[28px] font-bold text-[#23221e]">PetaCMS</h1>

        {state === 'verifying' && (
          <>
            <div className="mx-auto mt-6 h-10 w-10 animate-spin rounded-full border-4 border-[#edebe8] border-t-[#3b82f6]" />
            <p className="mt-4 text-[14px] text-[#706d65]">メールアドレスを確認中...</p>
          </>
        )}

        {state === 'success' && (
          <>
            <h2 className="mt-6 text-[16px] font-semibold text-[#23221e]">メール確認完了</h2>
            <p className="mt-3 text-[14px] text-[#706d65]">
              アカウントが有効化されました。ログインしてご利用ください。
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => window.location.assign('/login')}
                className="h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-[15px] font-semibold text-white transition hover:bg-[#1d4ed8]"
              >
                ログイン
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <h2 className="mt-6 text-[16px] font-semibold text-[#23221e]">確認に失敗しました</h2>
            <p className="mt-3 text-[14px] text-[#706d65]">
              {errorMsg || 'リンクが無効または期限切れです。'}
            </p>
            <div className="mt-6">
              <Link
                to="/login"
                className="text-[13px] font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
              >
                ログインページに戻る
              </Link>
            </div>
          </>
        )}

        {state === 'invalid' && (
          <>
            <h2 className="mt-6 text-[16px] font-semibold text-[#23221e]">無効なリンク</h2>
            <p className="mt-3 text-[14px] text-[#706d65]">メール確認リンクが無効です。</p>
            <div className="mt-6">
              <Link
                to="/login"
                className="text-[13px] font-medium text-[#3b82f6] hover:text-[#1d4ed8]"
              >
                ログインページに戻る
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
